import {
  Alert,
  Box,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  formatCurrency,
  formatDate,
  formatList,
  formatQueryError,
} from 'renderer/shared/formatters/formatters';
import { useListProductsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { capitalize } from 'lodash';
import { Discount } from '../schemas/discounts.schemas';
import { useListDiscountsQuery } from '../services/discounts.endpoints';

interface TableCellProps {
  name: string;
  value: string;
  size?: number;
}

const Cell = ({ name, value, size }: TableCellProps) => {
  return (
    <Grid container spacing={1} item direction="column" xs={size ?? 3}>
      <Grid item>
        <Typography variant="subtitle1" fontWeight="bold">
          {name}
        </Typography>
      </Grid>
      <Grid item>
        <Typography>{value}</Typography>
      </Grid>
    </Grid>
  );
};

interface DiscountsListProps {
  handleEditClick: (discount: Discount) => void;
  handleDeleteClick: (discount: Discount) => void;
}

const DiscountsList = ({
  // discounts,
  // error,
  // isLoading,
  handleEditClick,
  handleDeleteClick,
}: DiscountsListProps) => {
  const {
    data: discounts,
    isLoading: discountsLoading,
    error,
  } = useListDiscountsQuery();
  const { data: products } = useListProductsQuery({ includeHidden: true });
  const isLoading = discountsLoading || !products;

  const formatAmount = (discount: Discount) =>
    discount.amountUnits === 'dollars'
      ? formatCurrency(discount.amount)
      : `${discount.amount}%`;

  return (
    <Grid container item spacing={2} xs={12} sm={12}>
      {isLoading && (
        <>
          <Grid item>
            <Skeleton height={100} variant="rounded" width={500} />
          </Grid>
          <Grid item>
            <Skeleton height={100} variant="rounded" width={500} />
          </Grid>
        </>
      )}
      {discounts &&
        products &&
        discounts.map((discount) => (
          <Grid item xs={12} sm={12} key={discount.code}>
            <Paper
              elevation={1}
              sx={{
                padding: 2,
                my: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Grid container spacing={2} direction="row">
                <Grid item xs={12}>
                  <Typography variant="h5">
                    <strong>{formatAmount(discount)}</strong> Off{' '}
                    <strong>{capitalize(discount.duration)}</strong> With Code{' '}
                    <strong>{discount.code}</strong>
                  </Typography>
                </Grid>
                <Cell name="Description" value={discount.name} />
                <Cell
                  name="Minimum"
                  value={
                    discount.minimum === undefined
                      ? 'None'
                      : `${discount.minimum} ${discount.minimumUnits}`
                  }
                />
                <Cell
                  name="Active Between"
                  value={`${formatDate(
                    new Date(discount.start),
                  )} - ${formatDate(new Date(discount.end))}`}
                />
                <Cell
                  name="Products"
                  value={
                    discount.productIds.length
                      ? formatList(
                          discount.productIds.map((id) => {
                            const product = products.find((p) => p._id === id);
                            return product ? product.name : '';
                          }),
                        )
                      : 'All Products'
                  }
                />
              </Grid>
              <Box>
                <IconButton onClick={() => handleEditClick(discount)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDeleteClick(discount)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        ))}
      {error && (
        <Grid item xs={12}>
          <Alert severity="error">
            Error retrieving discounts: {formatQueryError(error)}
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default DiscountsList;
